import { cva } from 'class-variance-authority';

/**
 * Pagination list style definition.
 *
 * Provides styling for the `<ul>` element wrapping pagination items.
 * By default, it is a flex container with centered items and a medium gap.
 *
 * Variants:
 * - `justify`: Controls horizontal alignment of the pagination items.
 *   - `center` (default): Center-aligned.
 *   - `end`: Right-aligned.
 *   - `around`: Items distributed with space around.
 *   - `between`: Items distributed with space between.
 *
 * - `gap`: Controls horizontal spacing between items.
 *   - `sm`: Small spacing.
 *   - `md` (default): Medium spacing.
 *   - `lg`: Large spacing.
 *
 * Consumers can extend this to add additional variants (e.g. `orientation`, `wrap`) or
 * completely override the base behavior by providing a custom theme.
 *
 * Example:
 * ```ts
 * list: cva('flex flex-col gap-4', {
 *   variants: {
 *     orientation: {
 *       horizontal: 'flex-row',
 *       vertical: 'flex-col'
 *     }
 *   },
 *   defaultVariants: { orientation: 'horizontal' }
 * })
 * ```
 */
export const paginationList = cva('pagination__list flex list-none justify-center', {
  variants: {
    justify: {
      center: 'justify-center',
      end: 'justify-end',
      around: 'justify-around',
      between: 'justify-between'
    },
    gap: {
      sm: 'gap-2',
      md: 'gap-3',
      lg: 'gap-4'
    }
  },
  defaultVariants: {
    justify: 'center',
    gap: 'md'
  }
});

/**
 * Pagination item style definition.
 *
 * Applies to each `<li>` inside the pagination list.
 *
 * By default, items inherit minimal styling to act as flexible containers
 * for pagination buttons. This keeps layout consistent while allowing
 * full control of button styles separately.
 *
 * Consumers can extend this with additional variants or override defaults.
 * For example, you could add spacing, borders, or animations on each item.
 *
 * Example:
 * ```ts
 * item: cva('relative', {
 *   variants: {
 *     highlight: {
 *       true: 'ring-2 ring-blue-500',
 *       false: ''
 *     }
 *   },
 *   defaultVariants: { highlight: false }
 * })
 * ```
 */
export const paginationItem = cva('pagination__item');

/**
 * Pagination button style definition.
 *
 * Defines the base styling for all pagination buttons (numbers, prev/next, first/last).
 *
 * Built with `class-variance-authority` (CVA), this allows consistent styling across
 * all pagination controls while supporting variants for different button states:
 *
 * - `state`: Defines the visual style of the button.
 *   - `default`: Standard, interactive button state.
 *   - `active`: Highlighted state for the current page.
 *   - `disabled`: Muted state for non-interactive controls (e.g., "previous" on page 1).
 *
 * - `focused`: Defines focus ring styles for accessibility/keyboard navigation.
 *   - `true`: Applies an outline when focused.
 *   - `false`: Removes focus styles.
 *
 * Consumers may extend this with additional variants (e.g. `size`, `shape`) or override
 * styles entirely by providing a custom theme via the `PaginationProvider`.
 *
 * Example:
 * ```ts
 * button: cva('base-classes', {
 *   variants: {
 *     state: {
 *       active: 'bg-red-500'
 *     },
 *     size: {
 *       sm: 'h-6 w-6',
 *       lg: 'h-12 w-12'
 *     }
 *   },
 *   defaultVariants: { size: 'sm' }
 * })
 * ```
 */
export const paginationButton = cva(
  'pagination__button relative h-10 w-10 rounded-lg text-base font-bold hover:outline-black hover:text-black outline outline-1 select-none transition-colors duration-200',
  {
    variants: {
      state: {
        default: 'bg-gray-30 outline-gray-30 hover:bg-tertiary',
        active: 'bg-tertiary outline-black text-black',
        disabled: 'bg-white opacity-40 outline-black pointer-events-none',
        loading:
          'before:absolute before:size-3 before:rounded-full before:bg-primary before:animate-border-move-simple before:content-[""]'
      },
      focused: {
        true: 'focus:outline-2 focus:outline-black',
        false: ''
      }
    },
    defaultVariants: {
      state: 'default',
      focused: false
    }
  }
);

/**
 * Tailwind/CVA styling for pagination page-size controls.
 *
 * Groups styling for all elements involved in the page-size selector:
 * - `wrapper` – container for the page-size controls.
 * - `label` – text label describing the selector.
 * - `button` – buttons for selecting page size on large screens.
 * - `select` – dropdown for selecting page size on smaller screens.
 *
 * Variants:
 * - `button.state`:
 *   - `default` – unselected button style.
 *   - `active` – selected button style (bold, underlined, pointer-events disabled).
 *
 * Example usage:
 * ```ts
 * <div :class="theme.pageSize.wrapper()">
 *   <span :class="theme.pageSize.label()">Items per page:</span>
 *   <button :class="theme.pageSize.button({ state: 'active' })">20</button>
 *   <select :class="theme.pageSize.select()">
 *     <option>20</option>
 *   </select>
 * </div>
 * ```
 */
export const paginationPageSize = {
  wrapper: cva('pagination__page-size flex items-center justify-center gap-px mt-4'),
  sizeDescription: cva(''),
  button: cva(
    'all:unset relative px-2 py-1 border-0 cursor-pointer line-height-none text-center transition hover:underline hover:font-bold',
    {
      variants: {
        state: {
          default: '',
          active: 'pointer-events-none font-bold underline'
        }
      },
      defaultVariants: {
        state: 'default'
      }
    }
  ),
  selectLabel: cva('inline-flex items-center flex-wrap gap-2 font-medium'),
  select: cva('px-2 py-1 border border-gray-300 rounded bg-white cursor-pointer box-border w-auto text-center')
};

/**
 * Default theme for all pagination components
 */
const defaultPaginationTheme: PaginationTheme = {
  button: paginationButton,
  list: paginationList,
  item: paginationItem,
  pageSize: paginationPageSize
};

const PaginationThemeKey = Symbol('PaginationTheme');

/**
 * Provides a global pagination theme to all child components inside
 * a `PaginationProvider`.
 *
 * @param theme - A partial theme override. You can supply only the parts
 *                you want to customize (e.g., change `active` button classes)
 *                without redefining the full theme.
 *
 * This ensures all pagination buttons, numbers, and navigation controls
 * inside the provider use consistent styling.
 * If no custom theme is provided, `defaultPaginationTheme` will be used.
 *
 * Example usage:
 * ```vue
 * <PaginationProvider
 *   :theme="{
 *     button: cva('base-classes', { variants: { state: { active: 'bg-red-500' }}})
 *   }"
 * />
 * ```
 */
export function providePaginationTheme(theme?: Partial<PaginationTheme>) {
  const merged: PaginationTheme = {
    ...defaultPaginationTheme,
    ...theme
  } as PaginationTheme;
  provide(PaginationThemeKey, merged);
}

/**
 * Injects the currently active pagination theme.
 *
 * All pagination-related components (e.g., `PaginationButton`,
 * `PaginationNumbers`, `PaginationPrev`) call this composable
 * to resolve their styling.
 *
 * @returns The resolved `PaginationTheme` (either the defaults
 *          or a merged theme if a custom one was provided).
 */
export function usePaginationTheme(): PaginationTheme {
  return inject<PaginationTheme>(PaginationThemeKey, defaultPaginationTheme);
}
